/**
 * Auth context — the single source of truth for "who is logged in".
 *
 * Auth is cookie-only (wr_access/wr_refresh/wr_csrf). There is no token
 * in JS-accessible storage. On mount we call GET /api/v1/me; if the
 * access cookie is stale the api client transparently refreshes once.
 *
 * Shapes are sourced from what /me actually returns (MeResponse) — see
 * backend/internal/handlers/auth.go meResponse.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { authApi } from "@/lib/api";
import { queryClient } from "@/lib/query-client";
import type {
  LoginRequest,
  LoginResponse,
  LoginResult,
  MeResponse,
  MeTenant,
} from "@/types/auth";

// localStorage key used by query-client.ts's persister. Mirrored here
// so logout() can clear it independent of the import order (the
// persister is also keyed by this name).
const PERSISTED_QUERY_CACHE_KEY = "wr-query-cache";

interface AuthContextValue {
  /** The /me payload, or null when unauthenticated. */
  user: MeResponse | null;
  /** True until the initial /me probe resolves. */
  loading: boolean;
  /**
   * True when there IS an authenticated identity but NO active tenant
   * membership yet — the identity-only session that exists between a
   * >=2-membership login and the /auth/activate that picks a workspace.
   * The dashboard guard uses this to force the picker before any
   * tenant UI renders. (false while loading / signed out / tenant-ready.)
   */
  identityOnly: boolean;
  /**
   * Authenticate (email + password — ID.2 identity auth, no slug).
   * Returns the discriminated result so the caller can branch on:
   *  - `select_workspace` → the identity has >=2 memberships; an
   *    identity-only session cookie was set but there is NO active
   *    tenant. We hydrate /me (so `identityOnly` flips true) and the
   *    caller opens the picker → /auth/activate.
   *  - `totp_required` on a loginResponse → route to /2fa-verify.
   *  - otherwise → a full tenant session; /me is hydrated.
   */
  login: (body: LoginRequest) => Promise<LoginResult>;
  /**
   * Establish/switch the active tenant by slug (POST /auth/activate).
   * THE single path for both the post-login picker AND the in-app
   * instant switch — no password, no logout. On success it re-hydrates
   * /me so the whole app re-renders in the new tenant. Returns the
   * loginResponse so the caller can honour totp_required.
   */
  activate: (slug: string) => Promise<LoginResponse>;
  /** Clear the server session + local state. */
  logout: () => Promise<void>;
  /** Re-fetch /me (e.g. after enabling TOTP or switching company). */
  refresh: () => Promise<MeResponse | null>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<MeResponse | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async (): Promise<MeResponse | null> => {
    try {
      const me = await authApi.getMe();
      setUser(me);
      return me;
    } catch {
      // A 401 here just means "no usable session" (e.g. called after a
      // logout race). The api client already attempted its single
      // refresh-then-retry before this rejected, so treat it as
      // signed-out rather than re-driving auth.
      setUser(null);
      return null;
    }
  }, []);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        // probe:true sets _suppressAuthRedirect on the request config.
        // The response interceptor honours that flag: on 401 it neither
        // refreshes nor hard-redirects to /login — it just rejects, so
        // an anonymous visitor on a public page (/register, /forgot)
        // quietly resolves to user=null instead of being bounced to
        // /login?next=/register. (The earlier "suppresses path-wise"
        // comment was wrong — suppression is config-flag driven.)
        const me = await authApi.getMe({ probe: true });
        if (active) setUser(me);
      } catch {
        if (active) setUser(null);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  const login = useCallback(
    async (body: LoginRequest): Promise<LoginResult> => {
      const res = await authApi.login(body);
      // select_workspace: the identity has >=2 memberships. The backend
      // DID set an identity-only session cookie (no active tenant), so
      // we DO hydrate /me — it succeeds and `identity_id` is set with
      // no `tenant_id`, flipping `identityOnly` true. The caller then
      // opens the picker; /auth/activate completes the pick. (Hydrating
      // here means a refresh on the picker route keeps the user in the
      // identity-only state instead of looking signed-out.)
      await refresh();
      return res;
    },
    [refresh],
  );

  const activate = useCallback(
    async (slug: string): Promise<LoginResponse> => {
      // POST /auth/activate {slug} → full (rotated) tenant cookies.
      // Re-hydrate /me so the entire app (sidebar features, tenant
      // name, role-gated UI) re-renders in the newly active company —
      // no logout, no navigation to a re-auth screen.
      const res = await authApi.activate({ slug });
      // 2026-06-04 cross-tenant cache fix: drop every cached query
      // BEFORE refresh()/setUser propagate, so children that re-render
      // under the new tenant slug observe `idle` state and trigger
      // fresh fetches — never tenant-A rows under a tenant-B URL.
      // (Mirror of company-switcher's existing queryClient.clear()
      // — pulled into the canonical activate() so URL auto-switch +
      // post-login picker + in-app switcher all behave identically.)
      queryClient.clear();
      await refresh();
      return res;
    },
    [refresh],
  );

  const logout = useCallback(async (): Promise<void> => {
    try {
      await authApi.logout();
    } finally {
      setUser(null);
      // 2026-06-04 cross-tenant cache fix:
      // 1. Drop the in-memory react-query cache so nothing tenant-
      //    scoped survives the boundary.
      // 2. Drop the persisted cache from localStorage too — without
      //    this, a hard reload after logout would rehydrate the
      //    previous user's cache into the next signed-in identity.
      // The legacy `WR_PURGE_CACHES` postMessage targeted a service-
      // worker handler that doesn't exist in this codebase (dropped
      // during the Next.js → Vite migration); removed to avoid false
      // confidence.
      queryClient.clear();
      try {
        window.localStorage.removeItem(PERSISTED_QUERY_CACHE_KEY);
      } catch {
        /* localStorage disabled / quota — non-fatal */
      }
    }
  }, []);

  // Identity-only = authenticated human with NO resolved membership —
  // the >=2-membership login state before /auth/activate. The robust
  // discriminant is user_id===0: an identity-only /me has user_id 0 +
  // role "" + no tenant_id. A PLATFORM identity (super_admin) also has
  // no tenant_id but DOES have a real user_id + role, so keying on
  // user_id (not tenant_id) keeps platform users out of the picker.
  const identityOnly = user !== null && user.user_id === 0;

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      loading,
      identityOnly,
      login,
      activate,
      logout,
      refresh,
    }),
    [user, loading, identityOnly, login, activate, logout, refresh],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within <AuthProvider>");
  }
  return ctx;
}

/**
 * Tenant plan feature flags for the current user (from /me.features).
 * Use this to gate module nav/UI. Returns an array plus a `has` helper.
 */
export function useFeatures(): {
  features: string[];
  has: (feature: string) => boolean;
} {
  const { user } = useAuth();
  return useMemo(() => {
    const features = user?.features ?? [];
    const set = new Set(features);
    return { features, has: (f: string) => set.has(f) };
  }, [user]);
}

/** The current user's tenant block (from /me.tenant), or null. */
export function useTenant(): MeTenant | null {
  const { user } = useAuth();
  return user?.tenant ?? null;
}
